import { getCountryList, statesByCountry } from "@wayflyer/flyui";
import { useIntl } from "react-intl";
import { useStateRegion as useRegion, type PartialRecord } from "shared-utils";

import {
  getJurisdictionalLabelGetters,
  type Configuration as KycConfiguration,
  type KYCProductTypeFlow,
  type SupportedCountry as KycSupportedCountry,
  type SupportedCountry,
} from "..";
import type { ExternalReviewer } from "../api-types/company_data_schemas";
import type { DEAssociatedPersonsConfigrationParams } from "../api-types/jurisdictions/de_schemas";

const useJurisdictionType = (country: KycSupportedCountry): KycConfiguration["jurisdictionType"] => {
  const intl = useIntl();
  const lookup: Record<KycSupportedCountry, KycConfiguration["jurisdictionType"]> = {
    US: intl.formatMessage({ id: "kyc: state", defaultMessage: "state" }),
    IE: intl.formatMessage({ id: "kyc: jurisdiction", defaultMessage: "jurisdiction" }),
    GB: intl.formatMessage({ id: "kyc: jurisdiction", defaultMessage: "jurisdiction" }),
    AU: intl.formatMessage({ id: "kyc: jurisdiction", defaultMessage: "jurisdiction" }),
    CA: intl.formatMessage({ id: "kyc: jurisdiction", defaultMessage: "jurisdiction" }),
    ES: intl.formatMessage({ id: "kyc: jurisdiction", defaultMessage: "jurisdiction" }),
    NL: intl.formatMessage({ id: "kyc: jurisdiction", defaultMessage: "jurisdiction" }),
    BE: intl.formatMessage({ id: "kyc: jurisdiction", defaultMessage: "jurisdiction" }),
    DK: intl.formatMessage({ id: "kyc: jurisdiction", defaultMessage: "jurisdiction" }),
    SE: intl.formatMessage({ id: "kyc: jurisdiction", defaultMessage: "jurisdiction" }),
    NZ: intl.formatMessage({ id: "kyc: jurisdiction", defaultMessage: "jurisdiction" }),
    DE: intl.formatMessage({ id: "kyc: jurisdiction", defaultMessage: "jurisdiction" }),
    FR: intl.formatMessage({ id: "kyc: jurisdiction", defaultMessage: "jurisdiction" }),
  };
  return lookup[country];
};

const useJurisdictionName = (country: KycSupportedCountry): KycConfiguration["jurisdictionName"] => {
  const intl = useIntl();
  const lookup: Record<KycSupportedCountry, KycConfiguration["jurisdictionName"]> = {
    US: intl.formatMessage({ id: "the United States of America", defaultMessage: "the United States of America" }),
    IE: intl.formatMessage({ id: "The Republic of Ireland", defaultMessage: "The Republic of Ireland" }),
    GB: intl.formatMessage({ id: "the United Kingdom", defaultMessage: "the United Kingdom" }),
    AU: intl.formatMessage({ id: "Australia", defaultMessage: "Australia" }),
    CA: intl.formatMessage({ id: "Canada", defaultMessage: "Canada" }),
    ES: intl.formatMessage({ id: "Spain", defaultMessage: "Spain" }),
    NL: intl.formatMessage({ id: "Netherlands", defaultMessage: "Netherlands" }),
    BE: intl.formatMessage({ id: "Belgium", defaultMessage: "Belgium" }),
    DK: intl.formatMessage({ id: "Denmark", defaultMessage: "Denmark" }),
    SE: intl.formatMessage({ id: "Sweden", defaultMessage: "Sweden" }),
    NZ: intl.formatMessage({ id: "New Zealand", defaultMessage: "New Zealand" }),
    DE: intl.formatMessage({ id: "Germany", defaultMessage: "Germany" }),
    FR: intl.formatMessage({ id: "France", defaultMessage: "France" }),
  };
  return lookup[country];
};

const usePostalCodeLabelGetter = (): ((country: string) => KycConfiguration["postalCodeLabel"]) => {
  const getter = getJurisdictionalLabelGetters().getPostCodeLabel;
  return ((country: string) => getter(country as SupportedCountry)) as (country: string) => string;
};

/**
 * @deprecated This function is deprecated and will be removed in a future PR.
 * Please use the useStateRegion function within shared utils instead.
 */
export const useStateRegion = (country: KycSupportedCountry): KycConfiguration["stateRegion"] => {
  const intl = useIntl();
  const lookup: Record<KycSupportedCountry, KycConfiguration["stateRegion"]> = {
    US: {
      label: intl.formatMessage({ id: "kyc: State", defaultMessage: "State" }),
      options: statesByCountry["US"],
      requiredBySearch: true,
      searchOptionsLabel: intl.formatMessage({
        id: "US State of Registration",
        defaultMessage: "US State of Registration",
      }),
    },
    CA: {
      label: intl.formatMessage({ id: "kyc: Province", defaultMessage: "Province" }),
      options: statesByCountry["CA"],
      requiredBySearch: true,
      searchOptions: [
        { id: "AB", text: "Alberta" },
        { id: "BC", text: "British Columbia" },
        { id: "MB", text: "Manitoba" },
        { id: "NB", text: "New Brunswick" },
        { id: "NL", text: "Newfoundland and Labrador" },
        { id: "NT", text: "Northwest Territories" },
        { id: "NS", text: "Nova Scotia" },
        { id: "NU", text: "Nunavut" },
        { id: "ON", text: "Ontario" },
        { id: "PE", text: "Prince Edward Island" },
        { id: "QC", text: "Québec" },
        { id: "SK", text: "Saskatchewan" },
        { id: "YT", text: "Yukon" },
      ],
      searchOptionsLabel: intl.formatMessage({
        id: "Canadian Province of Incorporation",
        defaultMessage: "Canadian Province of Incorporation",
      }),
    },
    IE: {
      label: intl.formatMessage({
        id: "kyc: County",
        defaultMessage: "County",
      }),
      requiredBySearch: false,
    },
    GB: {
      label: intl.formatMessage({
        id: "kyc: County",
        defaultMessage: "County",
      }),
      requiredBySearch: false,
    },
    AU: {
      label: intl.formatMessage({
        id: "kyc: Territory",
        defaultMessage: "Territory",
      }),
      options: statesByCountry["AU"],
      requiredBySearch: false,
    },
    ES: {
      label: intl.formatMessage({ id: "kyc: Province", defaultMessage: "Province" }),
      requiredBySearch: false,
    },
    NL: {
      label: intl.formatMessage({ id: "kyc: Province", defaultMessage: "Province" }),
      options: statesByCountry["NL"],
      requiredBySearch: false,
    },
    BE: {
      label: intl.formatMessage({ id: "kyc: Province", defaultMessage: "Province" }),
      options: statesByCountry["BE"],
      requiredBySearch: false,
    },
    DK: {
      label: intl.formatMessage({ id: "kyc: Province", defaultMessage: "Province" }),
      requiredBySearch: false,
    },
    SE: {
      label: intl.formatMessage({ id: "kyc: Region", defaultMessage: "Region" }),
      requiredBySearch: false,
    },
    NZ: {
      label: intl.formatMessage({ id: "kyc: Region", defaultMessage: "Region" }),
      requiredBySearch: false,
    },
    DE: {
      label: intl.formatMessage({ id: "kyc: State", defaultMessage: "State" }),
      options: statesByCountry["DE"],
      requiredBySearch: false,
    },
    FR: {
      label: intl.formatMessage({ id: "kyc: Region", defaultMessage: "Region" }),
      options: statesByCountry["FR"],
      requiredBySearch: false,
    },
  };
  return lookup[country];
};

const useCompanyId = (country: KycSupportedCountry): KycConfiguration["companyIdentification"] => {
  const intl = useIntl();
  const lookup: Record<KycSupportedCountry, KycConfiguration["companyIdentification"]> = {
    US: {
      label: intl.formatMessage({
        id: "Employer Identification Number",
        defaultMessage: "Employer Identification Number",
      }),
      placeholder: "00-0000000",
      mask: "99-9999999",
    },
    IE: false,
    GB: false,
    AU: false,
    CA: false,
    ES: false,
    NL: false,
    BE: false,
    DK: false,
    SE: false,
    NZ: false,
    DE: {
      label: intl.formatMessage({
        id: "Company Tax ID (Steuernummer)",
        defaultMessage: "Company Tax ID (Steuernummer)",
      }),
      placeholder: "123/456/7890(0)(00)",
    },
    FR: false,
  };
  return lookup[country];
};

const useCompanyVat = (country: KycSupportedCountry): KycConfiguration["companyVat"] => {
  const intl = useIntl();
  const defaultLabel = intl.formatMessage({
    id: "VAT Registration Number",
    defaultMessage: "VAT Registration Number",
  });
  const defaultNoVatNumberOptOutLabel = intl.formatMessage({
    id: "My business does not have a VAT Registration Number",
    defaultMessage: "My business does not have a VAT Registration Number",
  });
  const lookup: Record<KycSupportedCountry, KycConfiguration["companyVat"]> = {
    US: false,
    IE: {
      label: defaultLabel,
      placeholder: "1234567A",
      optOutLabel: defaultNoVatNumberOptOutLabel,
    },
    GB: {
      label: defaultLabel,
      placeholder: "123456789",
      optOutLabel: defaultNoVatNumberOptOutLabel,
    },
    AU: {
      label: intl.formatMessage({
        id: "Australian Business Number (ABN)",
        defaultMessage: "Australian Business Number (ABN)",
      }),
      placeholder: "12345678901",
    },
    CA: {
      label: intl.formatMessage({
        id: "Business Number",
        defaultMessage: "Business Number",
      }),
      placeholder: "123456789",
    },
    ES: {
      label: defaultLabel,
      placeholder: "B12345678",
    },
    NL: {
      label: defaultLabel,
      placeholder: "123456789A01",
      optOutLabel: defaultNoVatNumberOptOutLabel,
    },
    BE: {
      label: defaultLabel,
      placeholder: "BE123456789",
    },
    DK: {
      label: defaultLabel,
      placeholder: "DK13585628",
    },
    SE: {
      label: defaultLabel,
      placeholder: "123456789012",
    },
    NZ: {
      label: intl.formatMessage({
        id: "NZ GST/IRD Number",
        defaultMessage: "NZ GST/IRD Number",
      }),
      placeholder: "12345678",
    },
    DE: {
      label: defaultLabel,
      placeholder: "DE123456789",
      optOutLabel: defaultNoVatNumberOptOutLabel,
    },
    FR: {
      label: defaultLabel,
      placeholder: "1234567890123",
    },
  };
  return lookup[country];
};

const usePersonId = (country: KycSupportedCountry): KycConfiguration["associatedPersonIdentification"] => {
  const intl = useIntl();
  const { getIdLabel } = getJurisdictionalLabelGetters();
  const lookup: Record<KycSupportedCountry, KycConfiguration["associatedPersonIdentification"]> = {
    US: {
      label: (selectedCountry: SupportedCountry = country) => getIdLabel(selectedCountry),
      placeholder: "000-00-0000",
      mask: "999-99-9999",
      optOutLabel: intl.formatMessage({
        id: "This person does not have a US social security number",
        defaultMessage: "This person does not have a US social security number",
      }),
    },
    IE: {
      label: (selectedCountry: SupportedCountry = country) => getIdLabel(selectedCountry),
      placeholder: "1234567A",
      optOutLabel: intl.formatMessage({
        id: "This person does not have an Irish PPS number",
        defaultMessage: "This person does not have an Irish PPS number",
      }),
    },
    GB: {
      label: (selectedCountry: SupportedCountry = country) => getIdLabel(selectedCountry),
      placeholder: "XX123456Y",
      optOutLabel: intl.formatMessage({
        id: "This person does not have a UK NIN",
        defaultMessage: "This person does not have a UK NIN",
      }),
    },
    AU: false,
    CA: false,
    ES: {
      label: (selectedCountry: SupportedCountry = country) => getIdLabel(selectedCountry),
      placeholder: "12345678X",
      optOutLabel: intl.formatMessage({
        id: "This person does not have a Spanish NIF",
        defaultMessage: "This person does not have a Spanish NIF",
      }),
    },
    NL: false,
    BE: {
      label: (selectedCountry: SupportedCountry = country) => getIdLabel(selectedCountry),
      placeholder: "12345678901",
      mask: "99999999999", //11 numbers
      optOutLabel: intl.formatMessage({
        id: "This person does not have a Belgian National Register number",
        defaultMessage: "This person does not have a Belgian National Register number",
      }),
    },
    DK: {
      label: (selectedCountry: SupportedCountry = country) => getIdLabel(selectedCountry),
      placeholder: "123456-7890",
      mask: "999999-9999",
      optOutLabel: intl.formatMessage({
        id: "This person does not have a Danish CPR",
        defaultMessage: "This person does not have a Danish CPR",
      }),
    },
    SE: {
      label: (selectedCountry: SupportedCountry = country) => getIdLabel(selectedCountry),
      placeholder: "123456-7890",
      // N.B. We can't use a `mask` here because these numbers can use
      // either 2 or 4 digits for the year of birth
      optOutLabel: intl.formatMessage({
        id: "This person does not have a Swedish Personnummer",
        defaultMessage: "This person does not have a Swedish Personnummer",
      }),
    },
    NZ: false,
    DE: {
      label: (selectedCountry: SupportedCountry = country) => getIdLabel(selectedCountry),
      placeholder: "12 345 678 901",
      mask: "99 999 999 999",
      optOutLabel: intl.formatMessage({
        id: "This person does not have a German Steuer-Identifikationsnummer",
        defaultMessage: "This person does not have a German Steuer-Identifikationsnummer",
      }),
    },
    FR: {
      label: (selectedCountry: SupportedCountry = country) => getIdLabel(selectedCountry),
      placeholder: "01 23 456 789 012",
      mask: "99 99 999 999 999",
      optOutLabel: intl.formatMessage({
        id: "This person does not have a French Numéro Fiscal de Référence",
        defaultMessage: "This person does not have a French Numéro Fiscal de Référence",
      }),
    },
  };
  return lookup[country];
};

const usePlaceOfBirth = (country: KycSupportedCountry): KycConfiguration["associatedPersonPlaceOfBirth"] => {
  const lookup: Record<KycSupportedCountry, KycConfiguration["associatedPersonPlaceOfBirth"]> = {
    AU: false,
    BE: {
      countryOfBirth: { required: true },
      cityOfBirth: { required: true },
    },
    CA: false,
    DE: {
      countryOfBirth: { required: true },
      cityOfBirth: { required: true },
    },
    DK: {
      countryOfBirth: { required: true },
      cityOfBirth: { required: true },
    },
    ES: {
      countryOfBirth: { required: true },
      cityOfBirth: { required: true },
    },
    FR: {
      countryOfBirth: { required: true },
      cityOfBirth: { required: true },
    },
    GB: {
      countryOfBirth: { required: true },
      cityOfBirth: { required: true },
    },
    IE: {
      countryOfBirth: { required: true },
      cityOfBirth: { required: true },
    },
    NL: {
      countryOfBirth: { required: true },
      cityOfBirth: { required: true },
    },
    NZ: false,
    SE: {
      countryOfBirth: { required: true },
      cityOfBirth: { required: true },
    },
    US: false,
  };
  return lookup[country];
};

const useJurisdictionSpecificFields = (
  country: KycSupportedCountry
): KycConfiguration["jurisdictionSpecificFields"] => {
  const intl = useIntl();
  const lookup: PartialRecord<KycSupportedCountry, KycConfiguration["jurisdictionSpecificFields"]> = {
    DE: {
      uboOwnershipPercentage: true,
      powerOfAttorney: true,
      taxResidency: getCountryList(),
      gender: [
        {
          id: "MALE",
          text: intl.formatMessage({
            id: "kyc: gender male",
            defaultMessage: "Male",
          }),
        },
        {
          id: "FEMALE",
          text: intl.formatMessage({
            id: "kyc: gender female",
            defaultMessage: "Female",
          }),
        },
        {
          id: "DIVERSE",
          text: intl.formatMessage({
            id: "kyc: gender diverse",
            defaultMessage: "Diverse",
          }),
        },
      ],
    },
    US: {
      phoneNumber: true,
      uboOwnershipPercentage: true,
    },
  };
  return lookup[country];
};

const useJurisdictionSpecificConfiguration = (
  country: KycSupportedCountry,
  externalReviewer: ExternalReviewer | null | undefined
): KycConfiguration["jurisdictionSpecificConfiguration"] => {
  const intl = useIntl();

  const deRaisinVerifyText = intl.formatMessage(
    {
      id: "By proceeding you agree to your personal data being shared according to the [Wayflyer Privacy Notice](https://www.wayflyer.com/privacy-notice), the [IDnow Privacy Policy](https: //www.idnow.io/privacy/) and the [Raisin Bank Privacy Policy](https://www.raisin.ie/data-protection/).",
      defaultMessage:
        "By proceeding you agree to your personal data being shared according to the [Wayflyer Privacy Notice](https://www.wayflyer.com/privacy-notice),{lineBreak}the [IDnow Privacy Policy](https://www.idnow.io/privacy/) and the [Raisin Bank Privacy Policy](https://www.raisin.ie/data-protection/).",
    },
    {
      lineBreak: "\n",
    }
  );

  const deSLAMVerifyText = intl.formatMessage(
    {
      id: "kyc: verify identity confirmation text DE SLAM",
      defaultMessage:
        "By proceeding you agree to your personal data being shared according to the [Wayflyer Privacy Notice]({wayflyerPrivacyPolicyLink}),{lineBreak}the [IDnow Privacy Policy]({idnowPrivacyPolicyLink}) and the [SLAM Privacy Policy]({slamPrivacyPolicyLink}).",
    },
    {
      lineBreak: "\n",
      wayflyerPrivacyPolicyLink: "https://www.wayflyer.com/privacy-notice",
      idnowPrivacyPolicyLink: "https://www.idnow.io/privacy/",
      slamPrivacyPolicyLink: "https://www.smartlenders-am.com/privacy-policy/",
    }
  );

  const lookup: PartialRecord<KycSupportedCountry, KycConfiguration["jurisdictionSpecificConfiguration"]> = {
    DE: {
      requiredUBOPercentage: 25.001,
      areMultipleSignersAllowed: true,
      getSigneeChangeParams: (value): DEAssociatedPersonsConfigrationParams => ({
        is_signing_contract: value?.isSignee,
        power_of_attorney: value?.powerOfAttorney,
      }),
      verifyIdentityText: {
        requiresInput: {
          title: intl.formatMessage({
            id: "Start identity verification",
            defaultMessage: "Start identity verification",
          }),
          text: intl.formatMessage({
            id: "kyc: verify identity text",
            defaultMessage:
              "During identity verification you will need a valid government-issued photo ID. If possible, please use an ID that includes your residential address (such as your national ID card or driver's license). If you use an ID that does not contain your residential address we may contact you in future for a proof of address document.",
          }),
        },
      },
      verifyWithConfirmation: {
        title: intl.formatMessage({
          id: "Wayflyer partners with IDnow for secure identity verification.",
          defaultMessage: "Wayflyer partners with IDnow for secure identity verification.",
        }),
        text: externalReviewer === "slam" ? deSLAMVerifyText : deRaisinVerifyText,
      },
      personStepDescription: intl.formatMessage({
        id: "kyc: information about an executive and each person DE",
        defaultMessage:
          "If no person owns greater than 25% of the business, please add all managing directors (Geschäftsführer) as associated persons to complete verification.",
      }),
      personsNoUbosDescription: intl.formatMessage({
        id: "kyc: confirm that no ubos description DE",
        defaultMessage:
          "I confirm that no person directly or indirectly owns greater than 25% of the business and that I have added all managing directors (Geschäftsführer) as associated persons.",
      }),
    },
  };
  return lookup[country];
};
const useAssociatedPersonUploadFile = (
  country: KycSupportedCountry
): KycConfiguration["associatedPersonUploadFile"] => {
  const intl = useIntl();
  const lookup: PartialRecord<KycSupportedCountry, KycConfiguration["associatedPersonUploadFile"]> = {
    DE: {
      title: intl.formatMessage({
        id: "Upload confirmation of address",
        defaultMessage: "Upload confirmation of address",
      }),
      text: intl.formatMessage(
        {
          id: "kyc: documents DE",
          defaultMessage:
            "Please provide a utility bill issued within the last 2 months as proof of address.{lineBreak}Acceptable utility bills include gas, electricity, water, phone or internet.",
        },
        {
          lineBreak: "\n",
        }
      ),
    },
  };
  return lookup[country];
};

const requirePersonNationality = (country: KycSupportedCountry): KycConfiguration["personNationality"] => {
  const lookup: PartialRecord<KycSupportedCountry, KycConfiguration["personNationality"]> = {
    DE: true,
    FR: true,
    US: true,
  };
  return lookup[country];
};

export const useKycConfiguration = (
  country: KycSupportedCountry,
  enabledProductFlows?: KYCProductTypeFlow[],
  externalReviewer?: ExternalReviewer | null
): KycConfiguration => {
  const getPostCodeLabel = usePostalCodeLabelGetter();
  return {
    country,
    countryOptions: getCountryList(),
    stateRegion: useRegion(country),
    companyIdentification: useCompanyId(country),
    companyVat: useCompanyVat(country),
    associatedPersonIdentification: usePersonId(country),
    postalCodeLabel: getPostCodeLabel(country),
    jurisdictionType: useJurisdictionType(country),
    jurisdictionName: useJurisdictionName(country),
    getPostCodeLabel,
    jurisdictionSpecificFields: useJurisdictionSpecificFields(country),
    jurisdictionSpecificConfiguration: useJurisdictionSpecificConfiguration(country, externalReviewer),
    associatedPersonUploadFile: useAssociatedPersonUploadFile(country),
    personNationality: requirePersonNationality(country),
    associatedPersonPlaceOfBirth: usePlaceOfBirth(country),
    enabledProductFlows: enabledProductFlows,
  };
};
